<script>
/* eslint-disable vue/no-bare-strings-in-template */
import { useAlert, useTrack } from 'dashboard/composables';
import ReportFilters from './ReportFilters.vue';
import ConversationsListTable from './ConversationsListTable.vue';
import EntityPerformanceDashboard from './entity/EntityPerformanceDashboard.vue';
import ReportDownloadMenu from './ReportDownloadMenu.vue';
import { GROUP_BY_FILTER } from '../constants';
import { REPORTS_EVENTS } from '../../../../../helper/AnalyticsHelper/events';
import { getLineChartFilters } from 'dashboard/helper/reportTimeRangeHelper';
import { generateFileName } from 'dashboard/helper/downloadHelper';

const GROUP_BY_OPTIONS = {
  DAY: [{ id: 1, groupByKey: 'REPORT.GROUPING_OPTIONS.DAY' }],
  WEEK: [
    { id: 1, groupByKey: 'REPORT.GROUPING_OPTIONS.DAY' },
    { id: 2, groupByKey: 'REPORT.GROUPING_OPTIONS.WEEK' },
  ],
  MONTH: [
    { id: 1, groupByKey: 'REPORT.GROUPING_OPTIONS.DAY' },
    { id: 2, groupByKey: 'REPORT.GROUPING_OPTIONS.WEEK' },
    { id: 3, groupByKey: 'REPORT.GROUPING_OPTIONS.MONTH' },
  ],
  YEAR: [
    { id: 2, groupByKey: 'REPORT.GROUPING_OPTIONS.WEEK' },
    { id: 3, groupByKey: 'REPORT.GROUPING_OPTIONS.MONTH' },
  ],
  CUSTOM: [
    { id: 1, groupByKey: 'REPORT.GROUPING_OPTIONS.DAY' },
    { id: 2, groupByKey: 'REPORT.GROUPING_OPTIONS.WEEK' },
    { id: 3, groupByKey: 'REPORT.GROUPING_OPTIONS.MONTH' },
    { id: 4, groupByKey: 'REPORT.GROUPING_OPTIONS.YEAR' },
  ],
};

export default {
  components: {
    ReportFilters,
    ConversationsListTable,
    EntityPerformanceDashboard,
    ReportDownloadMenu,
  },
  props: {
    type: {
      type: String,
      default: 'account',
    },
    getterKey: {
      type: String,
      default: '',
    },
    actionKey: {
      type: String,
      default: '',
    },
    downloadButtonLabel: {
      type: String,
      default: 'Download Reports',
    },
    selectedItem: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      from: 0,
      to: 0,
      selectedFilter: this.selectedItem,
      groupBy: GROUP_BY_FILTER[1],
      groupByfilterItemsList: GROUP_BY_OPTIONS.DAY.map(this.translateOptions),
      selectedGroupByFilter: null,
      businessHours: false,
      comunicanteCampaign: 'all',
      showDropdown: false, // Added for dropdown visibility
      conversationsPageIndex: 0, // For conversations list pagination
      selectedConversationMetric: '',
      selectedConversationMetricLabel: '',
      isExportingXlsx: false,
    };
  },
  computed: {
    filterItemsList() {
      return this.$store.getters[this.getterKey] || [];
    },
    isAgentType() {
      return this.type === 'agent';
    },
    selectedEntityName() {
      return this.selectedFilter?.name || this.selectedFilter?.title || '';
    },
    reportKeys() {
      return {
        CONVERSATIONS: 'conversations_count',
        ...(!this.isAgentType && {
          INCOMING_MESSAGES: 'incoming_messages_count',
        }),
        OUTGOING_MESSAGES: 'outgoing_messages_count',
        FIRST_RESPONSE_TIME: 'avg_first_response_time',
        RESOLUTION_TIME: 'avg_resolution_time',
        RESOLUTION_COUNT: 'resolutions_count',
        REPLY_TIME: 'reply_time',
      };
    },
  },
  mounted() {
    this.$store.dispatch(this.actionKey);
  },
  methods: {
    fetchAllData() {
      if (this.selectedFilter) {
        this.conversationsPageIndex = 0;
        const { from, to, groupBy, businessHours, comunicanteCampaign } = this;
        this.$store.dispatch('fetchAccountSummary', {
          from,
          to,
          type: this.type,
          id: this.selectedFilter.id,
          groupBy: groupBy.period,
          businessHours,
          comunicanteCampaign,
        });
        this.fetchConversationsList(0); // Fetch conversations on first page
        this.fetchChartData();
      }
    },
    fetchChartData() {
      const chartFilters = getLineChartFilters({
        from: this.from,
        to: this.to,
        groupBy: this.groupBy.period,
        businessHours: this.businessHours,
        comunicanteCampaign: this.comunicanteCampaign,
      });
      Object.keys(this.reportKeys).forEach(async key => {
        try {
          this.$store.dispatch('fetchAccountReport', {
            metric: this.reportKeys[key],
            from: chartFilters.from,
            to: chartFilters.to,
            type: this.type,
            id: this.selectedFilter.id,
            groupBy: chartFilters.groupBy,
            businessHours: chartFilters.businessHours,
            comunicanteCampaign: chartFilters.comunicanteCampaign,
          });
        } catch {
          useAlert(this.$t('REPORT.DATA_FETCHING_FAILED'));
        }
      });
    },
    downloadReports() {
      this.showDropdown = false;
      const { from, to, type, businessHours, comunicanteCampaign } = this;
      const dispatchMethods = {
        agent: 'downloadAgentReports',
        label: 'downloadLabelReports',
        inbox: 'downloadInboxReports',
        team: 'downloadTeamReports',
      };
      if (dispatchMethods[type]) {
        const fileName = generateFileName({ type, to, businessHours });
        const params = {
          from,
          to,
          fileName,
          businessHours,
          comunicanteCampaign,
        };
        this.$store.dispatch(dispatchMethods[type], params);
      }
    },
    downloadPDF() {
      this.showDropdown = false;
      this.$store.dispatch('setReportRef', this.$refs.reportContent);
      const { from, to, groupBy, selectedFilter, selectedGroupByFilter } = this;

      const path = this.$route.path.split('/').pop();
      const labels = {
        inboxes: 'da Caixa de Entrada',
        agents: 'dos Agentes',
        teams: 'das Equipes',
        label: 'dos Marcadores',
      };

      const typesLabel = {
        label: 'Marcador',
        agent: 'Agente',
        team: 'Equipe',
        inbox: 'Caixa de Entrada',
      };

      this.$store.dispatch('generatePDF', {
        from,
        to,
        groupBy,
        selectedFilter,
        type: typesLabel[this.type] || this.type,
        selectedGroupByFilter,
        currentUser: JSON.parse(JSON.stringify(this.currentUser)),
        title: labels[path] || 'dos Agentes',
      });
    },
    async downloadConversationsXlsx() {
      if (!this.selectedFilter || this.isExportingXlsx) return;

      this.isExportingXlsx = true;
      try {
        const { from, to, groupBy, businessHours, comunicanteCampaign } = this;
        await this.$store.dispatch('downloadConversationsListXlsx', {
          metric: this.selectedConversationMetric || 'conversations_count',
          from,
          to,
          type: this.type,
          id: this.selectedFilter.id,
          groupBy: groupBy.period,
          businessHours,
          comunicanteCampaign,
          fileName: `${this.type}-conversas.xlsx`,
        });
      } catch {
        useAlert(this.$t('REPORT.DATA_FETCHING_FAILED'));
      } finally {
        this.isExportingXlsx = false;
      }
    },
    onDateRangeChange({ from, to, groupBy }) {
      this.selectedFilter = this.selectedFilter || this.selectedItem;
      // do not track filter change on inital load
      if (this.from !== 0 && this.to !== 0) {
        useTrack(REPORTS_EVENTS.FILTER_REPORT, {
          filterType: 'date',
          reportType: this.type,
        });
      }

      this.from = from;
      this.to = to;
      this.groupByfilterItemsList = this.fetchFilterItems(groupBy);
      const filterItems = this.groupByfilterItemsList.filter(
        item => item.id === this.groupBy.id
      );
      if (filterItems.length > 0) {
        this.selectedGroupByFilter = filterItems[0];
      } else {
        this.selectedGroupByFilter = this.groupByfilterItemsList[0];
        this.groupBy = GROUP_BY_FILTER[this.selectedGroupByFilter.id];
      }
      this.fetchAllData();
    },
    onFilterChange(payload) {
      if (!payload) return;

      const filterChanged = this.selectedFilter?.id !== payload.id;
      this.selectedFilter = payload;
      if (filterChanged) {
        this.fetchAllData();
      }
    },
    onGroupByFilterChange(payload) {
      this.groupBy = GROUP_BY_FILTER[payload.id];
      this.fetchAllData();

      useTrack(REPORTS_EVENTS.FILTER_REPORT, {
        filterType: 'groupBy',
        filterValue: this.groupBy?.period,
        reportType: this.type,
      });
    },
    fetchFilterItems(groupBy) {
      switch (groupBy) {
        case GROUP_BY_FILTER[2].period:
          return GROUP_BY_OPTIONS.WEEK.map(this.translateOptions);
        case GROUP_BY_FILTER[3].period:
          return GROUP_BY_OPTIONS.MONTH.map(this.translateOptions);
        case GROUP_BY_FILTER[4].period:
          return GROUP_BY_OPTIONS.YEAR.map(this.translateOptions);
        case GROUP_BY_FILTER[5].period:
          return GROUP_BY_OPTIONS.CUSTOM.map(this.translateOptions);
        default:
          return GROUP_BY_OPTIONS.DAY.map(this.translateOptions);
      }
    },
    translateOptions(opts) {
      return { id: opts.id, groupBy: this.$t(opts.groupByKey) };
    },
    onBusinessHoursToggle(value) {
      this.businessHours = value;
      this.fetchAllData();

      useTrack(REPORTS_EVENTS.FILTER_REPORT, {
        filterType: 'businessHours',
        filterValue: value,
        reportType: this.type,
      });
    },
    onComunicanteCampaignChange(selectedCampaign) {
      this.comunicanteCampaign = selectedCampaign.id;
      this.fetchAllData();
    },
    fetchConversationsList(page = 0) {
      if (this.selectedFilter) {
        const { from, to, groupBy, businessHours, comunicanteCampaign } = this;
        this.$store.dispatch('fetchConversationsList', {
          metric: this.selectedConversationMetric || 'conversations_count',
          from,
          to,
          type: this.type,
          id: this.selectedFilter.id,
          page: page + 1, // API uses 1-based pagination
          groupBy: groupBy.period,
          businessHours,
          comunicanteCampaign,
        });
      }
    },
    onConversationsPageChange(pageIndex) {
      this.conversationsPageIndex = pageIndex;
      this.fetchConversationsList(pageIndex);
    },
    onConversationMetricSelect(metric) {
      const metricKey = metric?.key || '';
      const shouldClear = metricKey === this.selectedConversationMetric;
      this.selectedConversationMetric = shouldClear ? '' : metricKey;
      this.selectedConversationMetricLabel = shouldClear ? '' : metric.label;
      this.conversationsPageIndex = 0;
      this.fetchConversationsList(0);
    },
  },
};
</script>

<template>
  <div class="flex-1 p-4 overflow-auto">
    <ReportDownloadMenu
      class="button--fixed-top"
      :aria-label="downloadButtonLabel"
      :loading="isExportingXlsx"
      :xlsx-handler="downloadConversationsXlsx"
    />
    <ReportFilters
      v-if="filterItemsList"
      :type="type"
      :selected-filter="selectedItem"
      :filter-items-list="filterItemsList"
      :group-by-filter-items-list="groupByfilterItemsList"
      :selected-group-by-filter="selectedGroupByFilter"
      @date-range-change="onDateRangeChange"
      @filter-change="onFilterChange"
      @group-by-filter-change="onGroupByFilterChange"
      @comunicante-campaign-filter-selection="onComunicanteCampaignChange"
      @business-hours-toggle="onBusinessHoursToggle"
    />
    <div>
      <EntityPerformanceDashboard
        v-if="filterItemsList.length"
        :report-keys="reportKeys"
        :entity-name="selectedEntityName"
        :selected-metric="selectedConversationMetric"
        @metric-select="onConversationMetricSelect"
      />
      <!-- Conversations List Section -->
      <div v-if="filterItemsList.length" class="mt-8">
        <div class="mb-4">
          <h3 class="text-xl font-semibold text-slate-800 dark:text-slate-100">
            {{ $t('CONVERSATIONS_TABLE.HEADER.TITLE') || 'Conversas' }}
            <span v-if="selectedConversationMetricLabel">
              · {{ selectedConversationMetricLabel }}
            </span>
          </h3>
          <p class="text-sm text-slate-600 dark:text-slate-400">
            {{
              $t('CONVERSATIONS_TABLE.DESCRIPTION') ||
              'Lista de conversas baseada nos filtros selecionados'
            }}
          </p>
        </div>
        <ConversationsListTable
          :page-index="conversationsPageIndex"
          @page-change="onConversationsPageChange"
        />
      </div>
    </div>
  </div>
</template>
